import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Divider,
  Button,
  CircularProgress,
  styled,
  Chip,
  Grid,
  Modal,
  IconButton,
  Paper,
  TextField,
  Tab,
  Tabs,
  Alert,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemIcon,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Autocomplete,
  ButtonGroup,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  EmailOutlined as EmailIcon,
  PhoneAndroid as PhoneIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  PhotoLibrary as PhotoLibraryIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  CorporateFare as CorporateFareIcon,
  Work as WorkIcon,
  Edit as EditIcon,
  Restaurant as RestaurantIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import AccountingDataTable from '../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../accounting/shared/AccountingDataTable';
import ownAuthSession from '../../services/auth/ownAuthSession';
import firebaseDataService from '../../services/FirebaseDataService';
import { getQrMenusMap } from '../../services/migration/companyDataService';
import { getBranchById } from '../../services/migration/branchDataService';
import { taskDataService } from '../../services/taskDataService';
import { useAuth } from '../../contexts/AuthContext';
import { Html5Qrcode } from 'html5-qrcode';
import CompanyQRModal from './CompanyQRModal';
import PersonnelDetailsModal from './PersonnelDetailsModal';
import PersonnelActions from './PersonnelActions';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import { utils, writeFile } from 'xlsx';
import BranchQRModal from '../branches/BranchQRModal';
import CreatePersonnelModal from './CreatePersonnelModal';
import DepartmentModal from './DepartmentModal';
import { hrApi } from '../../config/apiClient';
import departmentDataService from '../../services/migration/departmentDataService';
import { getAllPersonnelBalances, PersonnelBalance } from '../../services/migration/personnelTransactionService';
import {
  consumePendingPersonnelFilters,
  PERSONNEL_FILTERS_EVENT,
  PersonnelFilterRequest,
} from '../aichat/agent/uiBridge';
import { useTranslation } from 'react-i18next';

// Personel kartı için styled component
const PersonnelCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
    cursor: 'pointer'
  }
}));

// Modal için styled component
const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// Personel ekleme modalı için styled component
const AddPersonnelModalContent = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '90%',
  maxWidth: 500,
  maxHeight: '80vh',
  overflow: 'auto',
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
}));

const TabPanel = (props: { children: React.ReactNode; value: number; index: number }) => {
  const { children, value, index } = props;
  return (
    <div role="tabpanel" hidden={value !== index} style={{ padding: '16px 0' }}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

// İnce personel kartı için styled component
const SlimPersonnelCard = styled(Card)(({ theme }) => ({
  borderRadius: 8,
  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
  margin: '4px 0',
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
    cursor: 'pointer'
  }
}));

// Personel için styled component tanımlamaları
const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// Silme onay modalı
const DeleteConfirmModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  personName: string;
  hasTask: boolean;
}> = ({ open, onClose, onConfirm, loading, personName, hasTask }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {tr('Personel Silme Onayı', 'Delete Personnel Confirmation')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          {isEnglish
            ? <>Are you sure you want to delete <b>{personName}</b>?</>
            : <><b>{personName}</b> adlı personeli silmek istediğinizden emin misiniz?</>}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {hasTask 
            ? tr('Bu personele aktif görev atanmış durumda. Lütfen önce görevi başka bir personele atayın.', 'This personnel has active tasks assigned. Please reassign the tasks first.')
            : tr('Bu işlem geri alınamaz ve personele ait tüm veriler silinecektir.', 'This action cannot be undone and all personnel data will be deleted.')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {tr('İptal', 'Cancel')}
        </Button>
        <Button 
          onClick={onConfirm} 
          color="error" 
          variant="contained" 
          disabled={loading || hasTask}
          startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          {loading ? tr('Siliniyor...', 'Deleting...') : tr('Evet, Sil', 'Yes, Delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Personel Bilgi Modalı
const PersonnelInfoModal: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #eee',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h6">{tr('Personel Sayfası Kullanımı', 'Personnel Page Guide')}</Typography>
        </Box>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
          {tr('Personel Sayfası Genel Bakış', 'Personnel Page Overview')}
        </Typography>
        <Typography paragraph>
          {tr(
            'Bu sayfada şirketinizin personellerini görüntüleyebilir, ekleyebilir, silebilir ve mesaj gönderebilirsiniz. Personel verilerini yönetmek için aşağıdaki özellikleri kullanabilirsiniz:',
            'On this page, you can view, add, delete, and message your company personnel. You can use the following features to manage personnel data:'
          )}
        </Typography>
        
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {tr('Kart ve Liste Görünümü', 'Card and List View')}
          </Typography>
          <Typography variant="body2" paragraph>
            {tr('Sağ üst köşedeki', 'Use the')} <ViewModuleIcon fontSize="small" sx={{ verticalAlign: 'middle' }}/> {tr('ve', 'and')} <ViewListIcon fontSize="small" sx={{ verticalAlign: 'middle' }}/> {tr('butonları ile personel listesini kart veya tablo şeklinde görüntüleyebilirsiniz.', 'buttons in the top-right corner to switch between card and table views.')}
          </Typography>
        </Box>
        
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {tr('Personel Ekleme', 'Adding Personnel')}
          </Typography>
          <Typography variant="body2" paragraph>
            {tr(
              'Yeni Personel Ekle butonu ile tek tek personel ekleyebilir veya QR kod ile personel kaydı yapabilirsiniz. Personel eklerken isim, telefon ve e-posta bilgilerini girebilirsiniz.',
              'With the New Personnel button, you can add personnel one by one or register via QR code. You can enter name, phone, and email details.'
            )}
          </Typography>
          <Typography variant="body2">
            {tr(
              'QR Kod Basma butonu ile de şirketinize özel QR kod oluşturabilir ve personellerinizin kolayca kaydolması için kullanabilirsiniz.',
              'With the Company QR button, you can generate a company-specific QR code for easy personnel registration.'
            )}
          </Typography>
        </Box>
        
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {tr('Personel Yönetimi', 'Personnel Management')}
          </Typography>
          <Typography variant="body2" paragraph>
            {tr('Her personelin kartında veya tablo satırında:', 'In each personnel card or table row:')}
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <PersonIcon fontSize="small" color="primary" />
              </ListItemAvatar>
              <ListItemText 
                primary={tr('Detaylar', 'Details')}
                secondary={tr('Personel detaylarını görüntülemek için personel kartına tıklayabilirsiniz.', 'Click the personnel card to view details.')} 
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemAvatar>
              <ListItemText 
                primary={tr('Silme', 'Delete')} 
                secondary={tr('Silme ikonu ile personeli sistemden kaldırabilirsiniz.', 'You can remove personnel from the system using the delete icon.')} 
              />
            </ListItem>
          </List>
        </Box>
        
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {tr('Durum İşaretleri', 'Status Indicators')}
          </Typography>
          <Typography variant="body2" paragraph>
            {tr('Personellerin görev durumlarını takip edebilirsiniz:', 'You can track personnel task status:')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Chip 
              label={tr('Görevi Yok', 'No Task')} 
              color="default" 
              size="small" 
              sx={{ mr: 2, fontSize: '11px', height: 24 }} 
            />
            <Typography variant="body2">
              {tr('Personele henüz görev atanmamış, yeni görev verebilirsiniz.', 'No task has been assigned yet; you can assign one.')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip 
              label={tr('Görev Atanmış', 'Task Assigned')} 
              color="primary" 
              size="small" 
              sx={{ mr: 2, fontSize: '11px', height: 24 }} 
            />
            <Typography variant="body2">
              {tr('Personel bir veya daha fazla göreve atanmış durumda.', 'Personnel is assigned to one or more tasks.')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          sx={{ borderRadius: 2 }}
        >
          {tr('Anladım', 'Understood')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Personnel arayüzü
interface PersonnelProps {
  branchId?: string;
  isManager?: boolean;
  /** Yönetici panelinde personel hareketleri yetkisi (personnel_transactions). */
  canTransact?: boolean;
  /** Yönetici panelinde kendi şubesine personel oluşturma yetkisi (personnel_create). */
  canCreate?: boolean;
}

const Personnel: React.FC<PersonnelProps> = ({ branchId, isManager = false, canTransact = false, canCreate = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  
  // ✅ AuthContext'ten branches, userDetails VE helper fonksiyonları al
  const { branches, userDetails, getPersonnelInfo, getBranchName } = useAuth();
  
  // ✅ Manager rolü kontrolü - prop üzerinden VEYA userDetails.role'dan
  // Manager kullanıcı Personnel sayfasına direkt erişirse de şube filtresi kilitlenir
  const isManagerRole = userDetails?.role === 'manager';
  const managerBranchId = branchId || (isManagerRole ? userDetails?.branchesId : undefined);
  
  // State tanımlamaları
  const [loading, setLoading] = useState(true);
  const [personnel, setPersonnel] = useState<any[]>([]);
  const [filteredPersonnel, setFilteredPersonnel] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [selectedPersonnel, setSelectedPersonnel] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [personnelTasks, setPersonnelTasks] = useState<any[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [personnelId, setPersonnelId] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [isAddingPersonnel, setIsAddingPersonnel] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list'); // Varsayılan görünüm 'list' olarak değiştirildi
  const [showDeleted, setShowDeleted] = useState(false); // Silinen personelleri gösterme durumu
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [companyQRModalOpen, setCompanyQRModalOpen] = useState(false);
  const [branchQRModalOpen, setBranchQRModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [branchName, setBranchName] = useState<string>('Şube');
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  // branches artık AuthContext'ten geliyor
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [selectedJobTitle, setSelectedJobTitle] = useState<string | null>(null);
  const [jobTitles, setJobTitles] = useState<{ id: string; name: string }[]>([]);
  const [isCreatePersonnelModalOpen, setIsCreatePersonnelModalOpen] = useState(false);
  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);
  // Sağ üst 3 nokta menüsü (Accounts ekranı ile aynı yapı)
  const [anchorElMore, setAnchorElMore] = useState<null | HTMLElement>(null);
  const handleOpenMoreMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMore(event.currentTarget);
  };
  const handleCloseMoreMenu = () => {
    setAnchorElMore(null);
  };
  
  // ✅ Manager rolündeki kullanıcı için şube filtresini otomatik kilitle
  useEffect(() => {
    if (isManagerRole && managerBranchId && selectedBranch !== managerBranchId) {
      setSelectedBranch(managerBranchId);
    }
  }, [isManagerRole, managerBranchId]);
  
  // Mobil için filtre görünürlük state'i
  const [showFilters, setShowFilters] = useState(false);

  // AI ajan köprüsü (openPersonnelPage): filtre state'lerini doldurur.
  // Manager modunda şube kilidi üstteki effect tarafından korunur (ajan aşamaz).
  useEffect(() => {
    const applyAgentRequest = (req: PersonnelFilterRequest) => {
      if (typeof req.searchText === 'string') setSearchTerm(req.searchText);
      if (req.branchId !== undefined && !isManagerRole) setSelectedBranch(req.branchId);
      if (req.role !== undefined) setSelectedRole(req.role);
      if (req.status !== undefined) setSelectedStatus(req.status);
      if (req.departmentId !== undefined) setSelectedDepartment(req.departmentId);
      if (typeof req.showDeleted === 'boolean') setShowDeleted(req.showDeleted);
    };
    const pending = consumePendingPersonnelFilters();
    if (pending) applyAgentRequest(pending);

    const onAgentEvent = (e: Event) => {
      consumePendingPersonnelFilters();
      applyAgentRequest((e as CustomEvent).detail as PersonnelFilterRequest);
    };
    window.addEventListener(PERSONNEL_FILTERS_EVENT, onAgentEvent);
    return () => window.removeEventListener(PERSONNEL_FILTERS_EVENT, onAgentEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManagerRole]);
  
  // Mutfak ekranı yetkileri için state'ler
  const [kitchenScreens, setKitchenScreens] = useState<{ id: string; name: string }[]>([]);
  const [hasKitchenAccess, setHasKitchenAccess] = useState(false);
  
  const navigate = useNavigate();

  // Yönetici paneli: /personnel/actions route'u admin'e özel olduğundan hareketler burada açılır
  const [selectedActionsPersonnelId, setSelectedActionsPersonnelId] = useState<string | null>(null);
  const openPersonnelActions = (personnelId: string) => {
    if (isManager) setSelectedActionsPersonnelId(personnelId);
    else navigate(`/personnel/actions/${personnelId}`);
  };
  const canOpenActions = !isManager || canTransact;
  const canCreatePersonnel = !isManager || (canCreate && !!branchId);
  const [reloadKey, setReloadKey] = useState(0);

  // Rol seçenekleri
  const roleOptions = [
    { id: 'admin', name: 'Admin' },
    { id: 'manager', name: tr('Yönetici', 'Manager') },
    { id: 'employee', name: tr('Personel', 'Personnel') }
  ];

  // Durum seçenekleri
  const statusOptions = [
    { id: 'hasTask', name: tr('Görevi Var', 'Assigned') },
    { id: 'available', name: tr('Görevi Yok', 'Available') }
  ];

  // Görünüm modunu localStorage'dan yükle
  useEffect(() => {
    const savedViewMode = localStorage.getItem('personnelViewMode');
    if (savedViewMode === 'list' || savedViewMode === 'card') {
      setViewMode(savedViewMode);
    } else {
      setViewMode('list'); // localStorage'da değer yoksa veya geçersizse varsayılan 'list'
      localStorage.setItem('personnelViewMode', 'list'); // localStorage'a da varsayılanı kaydet
    }
  }, []);

  // Görünüm modu değiştiğinde localStorage'a kaydet
  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewMode: 'card' | 'list' | null
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      localStorage.setItem('personnelViewMode', newViewMode);
    }
  };

  // Component mount olduğunda veri yükleme
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // ✅ companyId AuthContext cache'inden - Firebase sorgusu YOK!
        const companyId = userDetails?.companyId || null;
        setCompanyId(companyId);
        
        if (!companyId) {
          setLoading(false);
          return;
        }
        
        // Cloud Functions ile şubeleri ve personeli paralel yükle
        const [branchesData, personnelData] = await Promise.all([
          firebaseDataService.getBranches(companyId),
          firebaseDataService.getPersonnel(companyId)
        ]);
        
        if (!personnelData) {
          setPersonnel([]);
          setLoading(false);
          return;
        }
        
        // ✅ Cloud Functions ARRAY dönüyor - doğru şekilde işle
        // Array ise: her item'ın kendi 'id' alanı Firebase key'i içerir
        // Object ise: Object.entries ile key Firebase key olur
        
        let personnelList: any[];
        
        if (Array.isArray(personnelData)) {
          // ✅ ARRAY formatı - Cloud Functions'dan gelen yeni format
          console.log('📋 [Personnel] Array formatında veri işleniyor, toplam:', personnelData.length);
          
          personnelList = personnelData
            .filter((data: any) => {
              const isDeleted = data.isDeleted === true;
              return showDeleted ? isDeleted : !isDeleted;
            })
            .map((data: any) => ({
              id: data.id, // ✅ Firebase key item içindeki 'id' alanından gelir
              name: data.name || 'İsimsiz Personel',
              hasTask: data.hasTask || false,
              email: data.email || '',
              phone: data.phone || '',
              addedAt: data.addedAt || Date.now(),
              deletedAt: data.deletedAt || null,
              isDeleted: data.isDeleted || false,
              branchesId: data.branchesId || null,
              branchName: '',
              departmentId: data.departmentId || null,
              departmentName: '',
              jobTitle: data.jobTitle || '',
              role: data.role || ''
            }));
        } else {
          // ✅ OBJECT formatı - eski format (key: value)
          console.log('📋 [Personnel] Object formatında veri işleniyor');
          
          personnelList = await Promise.all(
            Object.entries(personnelData)
              .filter(([_, data]: [string, any]) => {
                const isDeleted = data.isDeleted === true;
                return showDeleted ? isDeleted : !isDeleted;
              })
              .map(async ([id, data]: [string, any]) => ({
                id, // ✅ Firebase key Object.entries'den gelir
                name: data.name || 'İsimsiz Personel',
                hasTask: data.hasTask || false,
                email: data.email || '',
                phone: data.phone || '',
                addedAt: data.addedAt || Date.now(),
                deletedAt: data.deletedAt || null,
                isDeleted: data.isDeleted || false,
                branchesId: data.branchesId || null,
                branchName: '',
                departmentId: data.departmentId || null,
                departmentName: '',
                jobTitle: data.jobTitle || '',
                role: data.role || ''
              }))
          );
        }
        
        console.log('📋 [Personnel] İşlenmiş personel listesi, ilk eleman:', personnelList[0]);
        
        // [SOURCE:SQL] Departmanları SQL'den yükle (Cloud Functions/RTDB kaldırıldı)
        const departmentsData = await departmentDataService.getDepartmentsMap(companyId);
        
        // ✅ Her personel için şube ve departman adlarını bul (AuthContext helper kullanılıyor!)
        const enhancedPersonnel = personnelList.map((person) => {
          // ✅ Şube adını AuthContext helper'ı ile al (tutarlı ve güvenilir)
          person.branchName = getBranchName(person.branchesId);
          
          // Departman ID'si varsa departman adını bul
          if (person.departmentId && departmentsData[person.departmentId as string]) {
            const departmentData = departmentsData[person.departmentId as string] as any;
            person.departmentName = departmentData.name || 'Bilinmeyen Departman';
          }
          
          return person;
        });
        
        // Admin personelleri liste dışında bırak ve gerekli filtrelemeleri uygula
        let sortedPersonnel = enhancedPersonnel.filter(person => person.role !== 'admin');
        
        // branchId prop'u varsa ve yönetici modundaysak sadece o şubenin personellerini göster
        // ve manager rolüne sahip personelleri filtrele
        // ✅ isManager prop'u VEYA userDetails.role === 'manager' ise filtre uygula
        if (isManager || (isManagerRole && managerBranchId)) {
          console.log("Şubeye ve role göre personel filtreleniyor:");
          
          // Giriş yapan kullanıcının UID'sini al
          const currentUserId = ownAuthSession.getCurrentUid();
          
          // Yönetici modunda, manager rolüne sahip personelleri ve giriş yapan kullanıcıyı filtreliyoruz
          sortedPersonnel = sortedPersonnel.filter(person => {
            // ✅ Giriş yapan yöneticiyi listeden çıkar (kendisini görmesin)
            if (currentUserId && person.id === currentUserId) {
              console.log(`Giriş yapan kullanıcı filtrelendi: ${person.fullName || person.id}`);
              return false;
            }
            
            // Önce role göre filtrele - manager rolüne sahip personelleri çıkar
            if (person.role === 'manager') {
              return false;
            }
            
            // Şube ID'sine göre filtrele (prop veya manager'ın kendi şubesi)
            const effectiveBranchId = branchId || managerBranchId;
            if (effectiveBranchId) {
              return person.branchesId === effectiveBranchId;
            }
            
            return true;
          });
        }
        
        // Ekleme tarihine göre sırala (yeniden eskiye)
        sortedPersonnel.sort((a, b) => b.addedAt - a.addedAt);

        // [SOURCE:SQL] Personel bakiyeleri (şirket geneli, tek istek) — PersonnelTransactions
        // perspektifi: pozitif => şirketin alacağı, negatif => şirketin borcu.
        // Hata olsa bile personel listesini engellemesin.
        try {
          const balanceMap = await getAllPersonnelBalances();
          sortedPersonnel.forEach((person) => {
            person.balances = balanceMap[person.id] || [];
          });
        } catch (balanceError) {
          console.error('Personel bakiyeleri yüklenirken hata:', balanceError);
          sortedPersonnel.forEach((person) => { person.balances = []; });
        }

        setPersonnel(sortedPersonnel);
        
        // NOT: Realtime listener kaldırıldı (offline mod için)
        // Güncellemeler için sayfayı yenileyin veya manuel refresh yapın
        
      } catch (error) {
        console.error('Personel verilerini yüklerken hata:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [showDeleted, branchId, isManager, userDetails?.companyId, reloadKey]); // userDetails.companyId değiştiğinde de çalışacak

  // Şubeler artık AuthContext'ten geliyor, ayrı yükleme gerekmiyor

  // Departmanları yükleme fonksiyonu
  useEffect(() => {
    const loadDepartments = async () => {
      if (!companyId) return;
      
      try {
        // [SOURCE:SQL] Departmanları SQL'den yükle (Cloud Functions/RTDB kaldırıldı)
        const departmentsList = await departmentDataService.getDepartments(companyId);
        setDepartments(departmentsList.map(d => ({ id: d.id, name: d.name })));
      } catch (error) {
        console.error('Departmanlar yüklenirken hata:', error);
      }
    };
    
    loadDepartments();
  }, [companyId]);

  // Meslekleri/Ünvanları yükleme fonksiyonu 
  useEffect(() => {
    // Personnel verisinden benzersiz meslek/ünvan listesi oluştur
    const uniqueJobTitles = new Set<string>();
    
    personnel.forEach(person => {
      if (person.jobTitle && person.jobTitle.trim() !== '') {
        uniqueJobTitles.add(person.jobTitle);
      }
    });
    
    // Set'ten array'e dönüştür ve formatla
    const jobTitlesList = Array.from(uniqueJobTitles).map(title => ({
      id: title,
      name: title
    }));
    
    // Alfabetik sırala
    jobTitlesList.sort((a, b) => a.name.localeCompare(b.name));
    
    setJobTitles(jobTitlesList);
  }, [personnel]);

  // Mutfak ekranı yetkilerini kontrol et
  useEffect(() => {
    const checkKitchenAccess = async () => {
      if (!companyId) return;
      
      try {
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) return;
        
        // [SOURCE:SQL] Mutfak ekranı yetkilerini SQL'den oku (Cloud Functions/RTDB kaldırıldı)
        const permResp = await hrApi.get(`/api/hr/personnel/${uid}/permissions/kitchenScreens`);
        const permItems: any[] = permResp.data?.items || [];
        const permittedKitchenScreens: Record<string, any> = {};
        for (const item of permItems) {
          if (!item?.screenId) continue;
          const settings = item.settings || {};
          permittedKitchenScreens[item.screenId] = {
            ...settings,
            accessKitchenScreen: settings.accessKitchenScreen ?? item.isEnabled ?? false,
          };
        }
        
        if (permittedKitchenScreens) {
          const kitchenMenuIds = Object.keys(permittedKitchenScreens || {});
          
          if (kitchenMenuIds.length > 0) {
            // QR menü bilgilerini SQL'den al (GET /api/QRMenu/menu-definitions)
            const qrMenusData = await getQrMenusMap(companyId);
            
            if (qrMenusData) {
              const availableScreens = kitchenMenuIds
                .filter(menuId => {
                  const permissions = permittedKitchenScreens[menuId];
                  return permissions?.accessKitchenScreen && qrMenusData[menuId];
                })
                .map(menuId => ({
                  id: menuId,
                  name: qrMenusData[menuId]?.name || `Menü ${menuId}`
                }));
              
              setKitchenScreens(availableScreens);
              setHasKitchenAccess(availableScreens.length > 0);
            }
          }
        }
      } catch (error) {
        console.error('Mutfak ekranı yetkileri kontrol edilirken hata:', error);
      }
    };
    
    checkKitchenAccess();
  }, [companyId]);

  // Filtreleme fonksiyonu
  useEffect(() => {
    let filtered = personnel;
    
    // Arama filtresi
    if (searchTerm.trim()) {
      filtered = filtered.filter(person => 
        person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (person.email && person.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (person.phone && person.phone.includes(searchTerm))
      );
    }
    
    // Şube filtresi
    if (selectedBranch) {
      if (selectedBranch === 'unassigned') {
        // Şubesi atanmamış personelleri filtrele
        filtered = filtered.filter(person => !person.branchesId);
      } else {
        // Belirli şubeye atanan personelleri filtrele
        filtered = filtered.filter(person => person.branchesId === selectedBranch);
      }
    }

    // Rol filtresi
    if (selectedRole) {
      filtered = filtered.filter(person => person.role === selectedRole);
    }

    // Durum filtresi
    if (selectedStatus) {
      filtered = filtered.filter(person => {
        if (selectedStatus === 'hasTask') {
          return person.hasTask;
        } else {
          return !person.hasTask;
        }
      });
    }
    
    // Departman filtresi
    if (selectedDepartment) {
      filtered = filtered.filter(person => person.departmentId === selectedDepartment);
    }
    
    // Meslek/Ünvan filtresi
    if (selectedJobTitle) {
      filtered = filtered.filter(person => person.jobTitle === selectedJobTitle);
    }
    
    setFilteredPersonnel(filtered);
  }, [searchTerm, selectedBranch, selectedRole, selectedStatus, selectedDepartment, selectedJobTitle, personnel]);

  // Personel detaylarını gösteren modal
  const handleOpenModal = async (person: any) => {
    try {
      // Eğer silinen personel ise hiçbir şey yapma
      if (person.isDeleted) {
        return;
      }
      
      console.log("Modal açılıyor, personel:", person);
      
      // Cloud Functions ile kullanıcı bilgilerini kontrol et
      if (person.id) {
        const userData = await firebaseDataService.getUserData(person.id);
        
        if (userData) {
          console.log("Kullanıcı verileri:", userData);
          
          // Users veritabanında branchesId var mı kontrol et
          if (userData.branchesId && !person.branchesId) {
            console.log("Kullanıcı veritabanında branchesId bulundu:", userData.branchesId);
            person.branchesId = userData.branchesId;
          }
        }
      }
      
    setSelectedPersonnel(person);
    setModalOpen(true);
    setConfirmDelete(false); // Reset delete confirmation state
    
    if (person.id) {
        setLoadingTasks(true);
        // Personele ait görevleri yükle
        await loadPersonnelTasks(person.id);
        }
      } catch (error) {
        console.error('Personel bilgileri yüklenirken hata:', error);
      } finally {
        setLoadingTasks(false);
    }
  };

  // Personele ait görevleri yükleyen fonksiyon
  const loadPersonnelTasks = async (personnelId: string) => {
    if (!companyId) return;
    
    setLoadingTasks(true);
    try {
      // Faz 5: Personele ait görevleri SQL'den yükle (taskDataService).
      const items: any[] = [];
      let tpage = 1;
      for (;;) {
        const res = await taskDataService.listTasks({ personnelId, page: tpage, pageSize: 500 });
        items.push(...res.items);
        if (res.items.length === 0 || items.length >= (res.totalCount || 0)) break;
        tpage++;
      }
      const personnelTasks = items.map((task: any) => ({
        ...task,
        id: task.legacyFirebaseKey || String(task.id),
        personnelId: task.personnelFirebaseUid || personnelId,
      }));
      setPersonnelTasks(personnelTasks);
    } catch (error) {
      console.error('Görevleri yüklerken hata:', error);
      setPersonnelTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Personel silme onay modalını göster
  const handleDeleteConfirm = () => {
    setConfirmDelete(true);
  };

  // Personel silme işlemi
  const handleDeletePersonnel = async () => {
    if (!selectedPersonnel || !ownAuthSession.getCurrentUid()) return;
    
    // Aktif görev kontrolü
    if (selectedPersonnel.hasTask) {
      setSnackbarMessage('Görev atanmış personel silinemez. Lütfen önce görevi başka bir personele atayın.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setDeleteModalOpen(false);
      return;
    }
    
    try {
      setLoading(true);
      
      await hrApi.post('/api/hr/personnel/delete', {
        FirebaseUid: selectedPersonnel.id
      });
      
      setSnackbarMessage('Personel başarıyla silindi');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      setDeleteModalOpen(false);
      setModalOpen(false);
      setPersonnel(prev => prev.filter(p => p.id !== selectedPersonnel.id));
    } catch (error: any) {
      console.error('Personel silinirken hata:', error);
      setSnackbarMessage(error.response?.data?.message || 'Personel silinirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Personel ekleme modalını açma
  const handleOpenAddModal = () => {
    setAddModalOpen(true);
    // Formları sıfırla
    setPersonnelId('');
    setBulkText('');
    setAddError(null);
    setAddSuccess(null);
    setTabValue(0);
  };

  // Modal tab değişimi
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setAddError(null);
    setAddSuccess(null);
  };

  // Personel ekleme temel fonksiyonu
  const addPersonnel = async (employeeId: string): Promise<void> => {
    if (!employeeId) {
      throw new Error('Personel ID gereklidir');
    }

    if (!companyId) {
      throw new Error('Şirket bilgisi bulunamadı');
    }

    try {
      await hrApi.post('/api/hr/personnel/add', { EmployeeId: employeeId });
    } catch (error: any) {
      console.error('Personel ekleme hatası:', error);
      throw new Error(error.response?.data?.message || 'Personel eklenirken bir hata oluştu');
    }
  };

  // Toplu personel ekleme işlemini güncelle
  const handleAddBulkPersonnel = async () => {
    if (!bulkText.trim()) {
      setAddError('Lütfen en az bir personel ID girin');
      return;
    }

    setIsAddingPersonnel(true);
    setAddError(null);
    setAddSuccess(null);

    try {
      // Metin alanından her satırı bir personel ID'si olarak al
      const lines = bulkText.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        throw new Error('Lütfen en az bir personel ID girin');
      }

      const response = await hrApi.post('/api/hr/personnel/add-bulk', {
        EmployeeIds: lines
      });
      
      const result = response.data;
      
      // Sonuçları göster
      if (result.results.success.length > 0) {
        setAddSuccess(`${result.results.success.length} personel başarıyla eklendi${result.results.failed.length > 0 ? `, ${result.results.failed.length} personel eklenemedi` : ''}`);
        setBulkText(''); // Metin alanını temizle
      } else {
        setAddError('Hiçbir personel eklenemedi');
      }
      
      // Personel limiti aşıldıysa uyarı göster
      if (result.limitExceeded) {
        setAddError(`Personel limitini aştınız. Kalan kontenjan: ${result.remainingSlots}. Sadece ilk ${result.remainingSlots} personel eklendi.`);
      }
    } catch (error: any) {
      setAddError(error.response?.data?.message || error.message || 'Toplu personel eklerken bir hata oluştu');
    } finally {
      setIsAddingPersonnel(false);
    }
  };

  // Tekli personel ekleme işlemi
  const handleAddSinglePersonnel = async () => {
    if (!personnelId.trim()) {
      setAddError('Lütfen bir Personel ID girin');
      return;
    }

    setIsAddingPersonnel(true);
    setAddError(null);
    setAddSuccess(null);

    try {
      await addPersonnel(personnelId.trim());
      setAddSuccess('Personel başarıyla eklendi');
      setPersonnelId('');
    } catch (error: any) {
      setAddError(error.message || 'Personel eklenirken bir hata oluştu');
    } finally {
      setIsAddingPersonnel(false);
    }
  };

  // İşe Geri Alma fonksiyonu
  const handleRestorePersonnel = async (personnelId: string) => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      
      await hrApi.post('/api/hr/personnel/restore', { FirebaseUid: personnelId });
      
      setSnackbarMessage('Personel başarıyla işe geri alındı');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Listeyi güncelle
      setPersonnel(prev => prev.filter(p => p.id !== personnelId));
    } catch (error: any) {
      console.error('Personel geri alınırken hata:', error);
      setSnackbarMessage(error.response?.data?.message || 'Personel geri alınırken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // QR kod resim yükleme
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsScanning(true);
    
    const html5QrCode = new Html5Qrcode("qr-reader");
    
    // QR kodu çözümle
    html5QrCode.scanFile(file, true)
      .then(decodedText => {
        setPersonnelId(decodedText);
        setIsScanning(false);
      })
      .catch(error => {
        console.error("QR kod tarama hatası:", error);
        setAddError("QR kod tanınamadı veya geçersiz. Lütfen başka bir resim deneyin.");
        setIsScanning(false);
      });
  };
  
  // QR kod resim yükleme butonunu tetikle
  const handleQrCodeImageScan = () => {
    fileInputRef.current?.click();
  };

  // Şube adını yükleme
  useEffect(() => {
    const loadBranchName = async () => {
      if (isManager && branchId && companyId) {
        try {
          // Şube bilgisini SQL'den al (GET /api/Branch)
          const branchData = await getBranchById(companyId, branchId);
          
          if (branchData) {
            // Şube adını kontrol et - iki farklı yapıda olabilir
            if ((branchData as any).name) {
              setBranchName((branchData as any).name);
            } else if ((branchData as any).basicInfo && (branchData as any).basicInfo.name) {
              setBranchName((branchData as any).basicInfo.name);
            }
          }
        } catch (error) {
          console.error('Şube adı yüklenirken hata:', error);
        }
      }
    };
    
    loadBranchName();
  }, [isManager, branchId, companyId]);
  
  // QR kodunu görüntülemek için modal açma/kapama işlevleri
  const handleOpenCompanyQRModal = () => {
    if (isManager && branchId) {
      setBranchQRModalOpen(true);
    } else {
      setCompanyQRModalOpen(true);
    }
  };

  const handleCloseCompanyQRModal = () => {
    setCompanyQRModalOpen(false);
  };
  
  const handleCloseBranchQRModal = () => {
    setBranchQRModalOpen(false);
  };

  // Mesaj sayfasına yönlendirme
  const handleSendMessage = (personnelId: string) => {
    // Personelin adını ve ID'sini mesaj sayfasına parametre olarak gönder
    navigate(`/messages?personnelId=${personnelId}`);
  };

  // Excel indirme fonksiyonu
  const handleDownloadExcel = () => {
    // Excel verilerini hazırla
    const excelData = personnel.map(person => ({
      [tr('Ad Soyad', 'Full Name')]: person.name,
      [tr('E-posta', 'Email')]: person.email || '-',
      [tr('Telefon', 'Phone')]: person.phone || '-',
      [tr('Durum', 'Status')]: showDeleted ? tr('Silinmiş', 'Deleted') : (person.hasTask ? tr('Görev Atanmış', 'Assigned') : tr('Müsait', 'Available')),
      [tr('Eklenme Tarihi', 'Created Date')]: new Date(person.addedAt).toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR'),
      [tr('Silinme Tarihi', 'Deleted Date')]: person.deletedAt ? new Date(person.deletedAt).toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR') : '-'
    }));

    // Excel çalışma kitabı oluştur
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(excelData);

    // Sütun genişliklerini ayarla
    const colWidths = [
      { wch: 30 },
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 }
    ];
    ws['!cols'] = colWidths;

    // Başlık stilini ayarla
    const headerStyle = {
      font: { bold: true },
      fill: { fgColor: { rgb: "CCCCCC" } },
      border: {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' }
      }
    };

    // Başlık hücrelerine stil uygula
    for (let i = 0; i < Object.keys(excelData[0]).length; i++) {
      const cellRef = utils.encode_cell({ r: 0, c: i });
      if (!ws[cellRef]) continue;
      ws[cellRef].s = headerStyle;
    }

    // Çalışma sayfasını kitaba ekle
    utils.book_append_sheet(wb, ws, showDeleted ? tr('Silinen Personeller', 'Deleted Personnel') : tr('Aktif Personeller', 'Active Personnel'));

    // Excel dosyasını indir
    writeFile(
      wb,
      `${showDeleted ? tr('Silinen_Personeller', 'Deleted_Personnel') : tr('Aktif_Personeller', 'Active_Personnel')}_${new Date().toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR').replace(/[./]/g, '-')}.xlsx`
    );
  };

  // Yeni modal için açma/kapama fonksiyonları
  const handleOpenCreatePersonnelModal = () => {
    setIsCreatePersonnelModalOpen(true);
  };

  const handleCloseCreatePersonnelModal = () => {
    setIsCreatePersonnelModalOpen(false);
    // Burada gerekirse form state'lerini sıfırlayabilirsiniz.
  };

  // Yeni Departman Modalı için açma/kapama fonksiyonları
  const handleOpenDepartmentModal = () => {
    setIsDepartmentModalOpen(true);
  };

  const handleCloseDepartmentModal = () => {
    setIsDepartmentModalOpen(false);
  };

  // Mutfak ekranına yönlendirme fonksiyonu
  const handleGoToKitchenScreen = () => {
    if (kitchenScreens.length > 0) {
      const firstKitchenScreen = kitchenScreens[0];
      navigate(`/mutfak-ekrani/${firstKitchenScreen.id}`);
    }
  };

  // Ortak tablo (AccountingDataTable) için kolon tanımları — eski PersonnelTable ile birebir
  const personnelColumns = useMemo<DataTableColumn<any>[]>(() => {
    const cols: DataTableColumn<any>[] = [
      {
        id: 'name',
        label: tr('Ad Soyad', 'Full Name'),
        field: 'name',
        sortable: true,
        tooltip: true,
        render: (row) => (
          <Typography variant="body2" fontWeight="medium" noWrap>
            {row.name}
          </Typography>
        ),
      },
      {
        id: 'departmentName',
        label: tr('Departman', 'Department'),
        field: 'departmentName',
        sortable: true,
        render: (row) => (
          row.departmentId
            ? <Typography variant="body2" noWrap>{row.departmentName || tr('Bilinmeyen Departman', 'Unknown Department')}</Typography>
            : <Typography variant="body2" color="text.secondary">-</Typography>
        ),
      },
      {
        id: 'jobTitle',
        label: tr('Meslek', 'Job Title'),
        field: 'jobTitle',
        sortable: true,
        render: (row) => <Typography variant="body2" noWrap>{row.jobTitle || '-'}</Typography>,
      },
      {
        id: 'phone',
        label: tr('Telefon', 'Phone'),
        field: 'phone',
        render: (row) => <Typography variant="body2" noWrap>{row.phone || '-'}</Typography>,
      },
      {
        id: 'email',
        label: tr('E-posta', 'Email'),
        field: 'email',
        tooltip: true,
        render: (row) => <Typography variant="body2" noWrap>{row.email || '-'}</Typography>,
      },
    ];

    if (!showDeleted) {
      cols.push({
        id: 'status',
        label: tr('Durum', 'Status'),
        sortable: true,
        field: 'hasTask',
        render: (row) => (
          <Chip
            size="small"
            label={row.hasTask ? tr('Görevi Var', 'Assigned') : tr('Görevi Yok', 'Available')}
            color={row.hasTask ? 'primary' : 'default'}
            sx={{ fontSize: '11px', height: 24 }}
          />
        ),
      });
    }

    cols.push(
      {
        id: 'branchName',
        label: tr('Şube', 'Branch'),
        field: 'branchName',
        sortable: true,
        render: (row) => (
          row.branchesId
            ? <Typography variant="body2" noWrap>{row.branchName || tr('Bilinmeyen Şube', 'Unknown Branch')}</Typography>
            : <Typography variant="body2" color="text.secondary">-</Typography>
        ),
      },
      {
        id: 'role',
        label: tr('Rol', 'Role'),
        field: 'role',
        sortable: true,
        render: (row) => (
          <Chip
            size="small"
            label={
              row.role === 'admin'
                ? tr('Admin', 'Admin')
                : row.role === 'manager'
                  ? tr('Yönetici', 'Manager')
                  : tr('Personel', 'Personnel')
            }
            color={row.role === 'admin' ? 'error' : row.role === 'manager' ? 'warning' : 'default'}
            sx={{ fontSize: '11px', height: 24, minWidth: 80 }}
          />
        ),
      },
      {
        id: 'balance',
        label: tr('Bakiye', 'Balance'),
        align: 'right',
        render: (row) => (
          (!row.balances || row.balances.length === 0)
            ? <Typography variant="body2" color="text.secondary">-</Typography>
            : (
              <>
                {row.balances.map((b: PersonnelBalance) => (
                  <Typography
                    key={b.currency}
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      color: b.balance > 0 ? 'success.main' : b.balance < 0 ? 'error.main' : 'text.secondary',
                    }}
                  >
                    {new Intl.NumberFormat(isEnglish ? 'en-US' : 'tr-TR', { style: 'currency', currency: b.currency || 'TRY' }).format(b.balance)}
                  </Typography>
                ))}
              </>
            )
        ),
      },
      {
        id: 'actions',
        label: tr('İşlemler', 'Actions'),
        align: 'right',
        width: '90px',
        render: (row) => (
          showDeleted || !canOpenActions
            ? <Typography variant="body2" color="text.secondary">-</Typography>
            : (
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  openPersonnelActions(row.id);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )
        ),
      },
    );

    return cols;
  }, [showDeleted, isEnglish, navigate, canOpenActions, isManager]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isManager && canTransact && selectedActionsPersonnelId) {
    return (
      <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 64px)', pb: 3 }}>
        <PersonnelActions
          key={selectedActionsPersonnelId}
          personnelId={selectedActionsPersonnelId}
          onBack={() => setSelectedActionsPersonnelId(null)}
          isManager
          managerBranchId={managerBranchId || undefined}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 },
      overflowY: 'auto',
      height: 'calc(100vh - 64px)',
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#555',
      },
    }}>
      {/* Başlık ve Butonlar - Responsive */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: 2,
        mb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant={isMobile ? "h5" : "h4"} component="h1" fontWeight="bold" color="primary">
            {tr('Personeller', 'Personnel')}
          </Typography>
        </Box>
        
        {/* Desktop Butonlar — Cashbox/Accounts/Inventory ile aynı yapı */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
          <IconButton
            color="primary"
            onClick={() => setShowFilters(!showFilters)}
            sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            <FilterListIcon />
          </IconButton>
          <ButtonGroup variant="contained">
            {canCreatePersonnel && (
              <Button
                startIcon={<PersonAddIcon />}
                onClick={handleOpenCreatePersonnelModal}
                disabled={showDeleted}
              >
                {tr('Personel Oluştur', 'Create Personnel')}
              </Button>
            )}
            <Button
              onClick={handleOpenMoreMenu}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              <MoreVertIcon />
            </Button>
          </ButtonGroup>
        </Box>

        {/* Mobil Butonlar — Cashbox/Accounts/Inventory ile aynı yapı */}
        <Grid container spacing={1} sx={{ display: { xs: 'flex', md: 'none' }, width: '100%' }} alignItems="center">
          {canCreatePersonnel && (
            <Grid item xs>
              <Button
                variant="contained"
                size="small"
                fullWidth
                startIcon={<PersonAddIcon />}
                onClick={handleOpenCreatePersonnelModal}
                disabled={showDeleted}
              >
                {tr('Personel Oluştur', 'Create Personnel')}
              </Button>
            </Grid>
          )}
          <Grid item xs="auto">
            <IconButton
              color="primary"
              onClick={() => setShowFilters(!showFilters)}
              sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
            >
              <FilterListIcon />
            </IconButton>
          </Grid>
          <Grid item xs="auto">
            <IconButton
              color="primary"
              onClick={handleOpenMoreMenu}
              sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
            >
              <MoreVertIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {/* Arama ve Filtre Barı */}
      {showFilters && (
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {isMobile ? (
            // Mobil: Grid Layout (2 sütun)
            <Grid container spacing={2} sx={{ width: '100%' }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={tr('Personel Ara...', 'Search Personnel...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  size="small"
                  options={[
                    { id: 'unassigned', name: tr('Şubesi Atanmamış', 'Unassigned Branch') },
                    ...branches
                  ]}
                  getOptionLabel={(option) => option.name}
                  value={selectedBranch === 'unassigned' 
                    ? { id: 'unassigned', name: tr('Şubesi Atanmamış', 'Unassigned Branch') } 
                    : branches.find(b => b.id === selectedBranch) || null}
                  onChange={(_, newValue) => {
                    // ✅ Manager rolündeki kullanıcı şube filtresini değiştiremez
                    if (isManagerRole && managerBranchId) return;
                    setSelectedBranch(newValue?.id || null);
                  }}
                  disabled={isManagerRole && !!managerBranchId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={isManagerRole && managerBranchId ? tr('Şubeniz', 'Your Branch') : tr('Şube', 'Branch')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  size="small"
                  options={departments}
                  getOptionLabel={(option) => option.name}
                  value={departments.find(d => d.id === selectedDepartment) || null}
                  onChange={(_, newValue) => setSelectedDepartment(newValue?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={tr('Departman', 'Department')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <CorporateFareIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  size="small"
                  options={jobTitles}
                  getOptionLabel={(option) => option.name}
                  value={jobTitles.find(j => j.id === selectedJobTitle) || null}
                  onChange={(_, newValue) => setSelectedJobTitle(newValue?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={tr('Meslek', 'Job Title')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <WorkIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  size="small"
                  options={roleOptions}
                  getOptionLabel={(option) => option.name}
                  value={roleOptions.find(r => r.id === selectedRole) || null}
                  onChange={(_, newValue) => setSelectedRole(newValue?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={tr('Rol', 'Role')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  size="small"
                  options={statusOptions}
                  getOptionLabel={(option) => option.name}
                  value={statusOptions.find(s => s.id === selectedStatus) || null}
                  onChange={(_, newValue) => setSelectedStatus(newValue?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={tr('Durum', 'Status')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <CheckCircleIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          ) : (
            // Desktop: Yan yana layout
            <>
              <TextField
                fullWidth
                size="small"
                placeholder={tr('Personel Ara...', 'Search Personnel...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  maxWidth: 300,
                  flexGrow: 1
                }}
              />
              
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                flexGrow: 1
              }}>
                <Autocomplete
                  size="small"
                  options={[
                    { id: 'unassigned', name: tr('Şubesi Atanmamış', 'Unassigned Branch') },
                    ...branches
                  ]}
                  getOptionLabel={(option) => option.name}
                  value={selectedBranch === 'unassigned' 
                    ? { id: 'unassigned', name: tr('Şubesi Atanmamış', 'Unassigned Branch') } 
                    : branches.find(b => b.id === selectedBranch) || null}
                  onChange={(_, newValue) => {
                    // ✅ Manager rolündeki kullanıcı şube filtresini değiştiremez
                    if (isManagerRole && managerBranchId) return;
                    setSelectedBranch(newValue?.id || null);
                  }}
                  disabled={isManagerRole && !!managerBranchId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={isManagerRole && managerBranchId ? tr('Şubeniz (Kilitli)', 'Your Branch (Locked)') : tr('Şube Seçin', 'Select Branch')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  sx={{ minWidth: 200 }}
                />

                <Autocomplete
                  size="small"
                  options={departments}
                  getOptionLabel={(option) => option.name}
                  value={departments.find(d => d.id === selectedDepartment) || null}
                  onChange={(_, newValue) => setSelectedDepartment(newValue?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={tr('Departman Seçin', 'Select Department')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <CorporateFareIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  sx={{ minWidth: 200 }}
                />

                <Autocomplete
                  size="small"
                  options={jobTitles}
                  getOptionLabel={(option) => option.name}
                  value={jobTitles.find(j => j.id === selectedJobTitle) || null}
                  onChange={(_, newValue) => setSelectedJobTitle(newValue?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={tr('Meslek/Ünvan Seçin', 'Select Job Title')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <WorkIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  sx={{ minWidth: 200 }}
                />

                <Autocomplete
                  size="small"
                  options={roleOptions}
                  getOptionLabel={(option) => option.name}
                  value={roleOptions.find(r => r.id === selectedRole) || null}
                  onChange={(_, newValue) => setSelectedRole(newValue?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={tr('Rol Seçin', 'Select Role')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  sx={{ minWidth: 200 }}
                />

                <Autocomplete
                  size="small"
                  options={statusOptions}
                  getOptionLabel={(option) => option.name}
                  value={statusOptions.find(s => s.id === selectedStatus) || null}
                  onChange={(_, newValue) => setSelectedStatus(newValue?.id || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={tr('Durum Seçin', 'Select Status')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <CheckCircleIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  sx={{ minWidth: 200 }}
                />
              </Box>
            </>
          )}
        </Box>
      )}
      
      {/* Personel Listesi */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredPersonnel.length === 0 ? (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            mt: 4,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 1
          }}
        >
          <PersonIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {searchTerm ? tr('Arama sonucu bulunamadı', 'No search results found') : (showDeleted ? tr('Silinen personel bulunmuyor', 'No deleted personnel found') : tr('Henüz personel bulunmuyor', 'No personnel yet'))}
          </Typography>
        </Box>
      ) : (isMobile || viewMode === 'card') ? (
        <Grid container spacing={2}>
          {filteredPersonnel.map((person) => (
            <Grid item xs={12} sm={6} md={3} key={person.id}>
              <PersonnelCard onClick={() => !showDeleted && handleOpenModal(person)}>
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Avatar 
                        sx={{ 
                          width: 50, 
                          height: 50, 
                          bgcolor: person.fcmToken ? '#4caf50' : '#f44336',
                          fontSize: 22
                        }}
                      >
                        {person.name?.charAt(0) || 'P'}
                      </Avatar>
                      <Box sx={{ ml: 1.5 }}>
                        <Typography variant="h6" fontWeight="bold" noWrap>
                          {person.name}
                        </Typography>
                        {!showDeleted && (
                          <Chip
                            label={person.hasTask ? tr('Görev Atanmış', 'Assigned') : tr('Müsait', 'Available')}
                            size="small"
                            color={person.hasTask ? 'primary' : 'success'}
                            sx={{ fontWeight: 'medium', mt: 0.5 }}
                          />
                        )}
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    {person.departmentId && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <CorporateFareIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {person.departmentName || tr('Bilinmeyen Departman', 'Unknown Department')}
                        </Typography>
                      </Box>
                    )}
                    
                    {person.jobTitle && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <WorkIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {person.jobTitle}
                        </Typography>
                      </Box>
                    )}
                    
                    {person.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {person.email}
                        </Typography>
                      </Box>
                    )}
                    
                    {person.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {person.phone}
                        </Typography>
                      </Box>
                    )}
                    
                    {person.branchesId && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {person.branchName || tr('Bilinmeyen Şube', 'Unknown Branch')}
                        </Typography>
                      </Box>
                    )}
                    
                    {!showDeleted && person.hasTask && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Typography variant="body2" color="primary">
                          {tr('Aktif görevleri var', 'Has active tasks')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </PersonnelCard>
            </Grid>
          ))}
        </Grid>
      ) : (
        <AccountingDataTable<any>
          columns={personnelColumns}
          rows={filteredPersonnel}
          getRowId={(row) => row.id}
          loading={loading}
          defaultRowsPerPage={50}
          rowsPerPageOptions={[25, 50, 100]}
          onRowClick={showDeleted ? undefined : (row) => handleOpenModal(row)}
          clickable={!showDeleted}
          emptyMessage={showDeleted ? tr('Silinen personel bulunmuyor', 'No deleted personnel found') : tr('Personel bulunamadı', 'No personnel found')}
          emptyIcon={<PersonIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
          columnResizeStorageKey="personnel"
          sx={{ mt: 2 }}
        />
      )}
      
      {/* Personel Ayrıntıları Modalı */}
      <PersonnelDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedPersonnel={selectedPersonnel}
        personnelTasks={personnelTasks}
        loadingTasks={loadingTasks}
        showDeleted={showDeleted}
        confirmDelete={confirmDelete}
        onDeleteConfirm={handleDeleteConfirm}
        onCancelDelete={() => setConfirmDelete(false)}
        onDeletePersonnel={handleDeletePersonnel}
        isManager={isManager}
        onOpenTransactions={isManager
          ? (canTransact
            ? (personnelId: string) => { setModalOpen(false); setSelectedActionsPersonnelId(personnelId); }
            : null)
          : undefined}
      />
      
      {/* Personel Ekleme Modalı */}
      <StyledModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        aria-labelledby="personel-ekleme-modal"
      >
        <AddPersonnelModalContent>
          <ModalHeader>
            <Typography variant="h5" fontWeight="bold">
              {tr('Personel Ekle', 'Add Personnel')}
            </Typography>
            <IconButton onClick={() => setAddModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </ModalHeader>
          
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab 
              icon={<PersonAddIcon />} 
              label={tr('Tek Personel', 'Single')} 
              iconPosition="start"
            />
            <Tab 
              icon={<GroupIcon />} 
              label={tr('Toplu Ekle', 'Bulk Add')} 
              iconPosition="start"
            />
          </Tabs>
          
          {addError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddError(null)}>
              {addError}
            </Alert>
          )}
          
          {addSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAddSuccess(null)}>
              {addSuccess}
            </Alert>
          )}
          
          <TabPanel value={tabValue} index={0}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tr('Eklemek istediğiniz personelin ID\'sini girin. Personel uygulama üzerinden kayıt olmuş olmalıdır.', 'Enter the ID of the personnel you want to add. They must already be registered in the personnel app.')}
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <TextField
                label={tr('Personel ID', 'Personnel ID')}
                fullWidth
                value={personnelId}
                onChange={(e) => setPersonnelId(e.target.value)}
                margin="normal"
                variant="outlined"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleQrCodeImageScan}
                startIcon={<PhotoLibraryIcon />}
                sx={{ mt: 1 }}
                disabled={isScanning}
              >
                {isScanning ? tr('Taranıyor...', 'Scanning...') : tr('Galeriden QR Kod Tara', 'Scan QR from Gallery')}
              </Button>
              
              {/* Gizli dosya yükleme input'u */}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
              
              {/* QR kod tarayıcısı için gizli div */}
              <div id="qr-reader" style={{ display: 'none' }}></div>
            </Box>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddSinglePersonnel}
                disabled={isAddingPersonnel || !personnelId.trim() || isScanning}
                startIcon={isAddingPersonnel ? <CircularProgress size={20} /> : <PersonAddIcon />}
              >
                {isAddingPersonnel ? tr('Ekleniyor...', 'Adding...') : tr('Personel Ekle', 'Add Personnel')}
              </Button>
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tr('Toplu personel eklemek için ID\'leri alt alta yazın. Her satırda bir personel ID\'si olmalıdır.', 'To add personnel in bulk, enter IDs line by line. Each line should contain one personnel ID.')}
            </Typography>
            
            <TextField
              label={tr('Personel ID listesi', 'Personnel ID list')}
              fullWidth
              multiline
              rows={6}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={tr(`Örnek:
123456
789012
345678`, `Example:
123456
789012
345678`)}
              variant="outlined"
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddBulkPersonnel}
                disabled={isAddingPersonnel || !bulkText.trim()}
                startIcon={isAddingPersonnel ? <CircularProgress size={20} /> : <GroupIcon />}
              >
                {isAddingPersonnel ? tr('Yükleniyor...', 'Loading...') : tr('Toplu Ekle', 'Bulk Add')}
              </Button>
            </Box>
          </TabPanel>
        </AddPersonnelModalContent>
      </StyledModal>

      {/* Şirket QR Modal - Normal kullanıcılar için */}
      {!isManager && (
        <CompanyQRModal 
          open={companyQRModalOpen} 
          onClose={handleCloseCompanyQRModal} 
        />
      )}
      
      {/* Şube QR Modal - Yöneticiler için */}
      {isManager && branchId && (
        <BranchQRModal 
          open={branchQRModalOpen} 
          onClose={handleCloseBranchQRModal} 
          branchId={branchId}
          branchName={branchName}
        />
      )}

      {/* Silme Onay Modalı */}
      <DeleteConfirmModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeletePersonnel}
        loading={loading}
        personName={selectedPersonnel?.name || ''}
        hasTask={selectedPersonnel?.hasTask || false}
      />

      {/* Personel Bilgi Modalı */}
      <PersonnelInfoModal 
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
      />

      {/* Snackbar bileşeni */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* YENİ PERSONEL OLUŞTURMA MODALI */}
      <CreatePersonnelModal 
        open={isCreatePersonnelModalOpen} 
        onClose={handleCloseCreatePersonnelModal} 
        companyId={companyId} 
        onPersonnelCreated={() => setReloadKey((k) => k + 1)}
        isManager={isManager}
        lockedBranchId={isManager ? branchId || null : null}
      />

      {/* DEPARTMAN MODALI */}
      <DepartmentModal 
        open={isDepartmentModalOpen} 
        onClose={handleCloseDepartmentModal} 
      />

      {/* Sağ üst 3 nokta menüsü */}
      <Menu
        anchorEl={anchorElMore}
        open={Boolean(anchorElMore)}
        onClose={handleCloseMoreMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          selected={viewMode === 'card'}
          onClick={() => {
            handleCloseMoreMenu();
            handleViewModeChange({} as React.MouseEvent<HTMLElement>, 'card');
          }}
        >
          <ListItemIcon>
            <ViewModuleIcon fontSize="small" color={viewMode === 'card' ? 'primary' : undefined} />
          </ListItemIcon>
          <ListItemText primary={tr('Kart Görünümü', 'Card View')} />
        </MenuItem>
        <MenuItem
          selected={viewMode === 'list'}
          onClick={() => {
            handleCloseMoreMenu();
            handleViewModeChange({} as React.MouseEvent<HTMLElement>, 'list');
          }}
        >
          <ListItemIcon>
            <ViewListIcon fontSize="small" color={viewMode === 'list' ? 'primary' : undefined} />
          </ListItemIcon>
          <ListItemText primary={tr('Liste Görünümü', 'List View')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseMoreMenu();
            setInfoModalOpen(true);
          }}
        >
          <ListItemIcon>
            <InfoIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText primary={tr('Bilgi', 'Info')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseMoreMenu();
            handleOpenCompanyQRModal();
          }}
        >
          <ListItemIcon>
            <QrCode2Icon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText primary={tr('Şirket QR', 'Company QR')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseMoreMenu();
            handleDownloadExcel();
          }}
          disabled={personnel.length === 0}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText primary={tr('Excel İndir', 'Download Excel')} />
        </MenuItem>
        {!isManager && (
          <MenuItem
            onClick={() => {
              handleCloseMoreMenu();
              handleOpenDepartmentModal();
            }}
            disabled={showDeleted}
          >
            <ListItemIcon>
              <CorporateFareIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText primary={tr('Departman Ekle', 'Add Department')} />
          </MenuItem>
        )}
        {!isManager && (
          <MenuItem
            onClick={() => {
              handleCloseMoreMenu();
              handleOpenAddModal();
            }}
            disabled={showDeleted}
          >
            <ListItemIcon>
              <AddIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary={tr('Yeni Ekle', 'Add New')} />
          </MenuItem>
        )}
        {hasKitchenAccess && (
          <MenuItem
            onClick={() => {
              handleCloseMoreMenu();
              handleGoToKitchenScreen();
            }}
          >
            <ListItemIcon>
              <RestaurantIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText primary={tr('Mutfak Ekranı', 'Kitchen Screen')} />
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleCloseMoreMenu();
            setShowDeleted(!showDeleted);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={showDeleted ? tr('Aktif Personeller', 'Active Personnel') : tr('Silinen Personeller', 'Deleted Personnel')} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Personnel; 